// NodeDateTimeRangeModal.jsx

import React, { useEffect, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { normalizeRangeForSave } from 'node/form/fields/utilities/dateFieldUtils';

// CSS
import './NodeDateTimeRangeModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import DateTimeRangeRepeatEditor from 'node/form/fields/datetimerange/DateTimeRangeRepeatEditor';
import NodeDateTimeRangeInput from 'node/form/fields/datetimerange/NodeDateTimeRangeInput';

/**
 * NodeDateTimeRangeModal
 *
 * Modal editor for schema-driven datetime range fields.
 *
 * @param {object} value - Current field value.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onChange - Callback when the value is saved.
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeDateTimeRangeModal = ({ value, isOpen, setOpen, onChange }) => {

  // ----- STATE ----- //

  const [dateTimeRange, setDateTimeRange] = useState(null);

  // ----- REFS ----- //

  const dateTimeRangeRef = useRef(null);

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isOpen) return;
    const nextValue = value ? { ...value } : null;
    dateTimeRangeRef.current = nextValue;
    setDateTimeRange(nextValue);
  }, [isOpen, value]);

  // ----- HANDLERS ----- //

  const handleDateTimeRangeChange = (nextValue) => {
    const previous = dateTimeRangeRef.current;
    let merged = nextValue;
    if (nextValue && previous?.recurrence) {
      merged = { ...nextValue, recurrence: previous.recurrence };
    } else if (nextValue == null && previous?.recurrence) {
      merged = null;
    }
    dateTimeRangeRef.current = merged;
    setDateTimeRange(merged);
  };

  const handleRepeatChange = (nextValue) => {
    dateTimeRangeRef.current = nextValue;
    setDateTimeRange(nextValue);
  };

  /**
 * Handles save.
 */
  const handleSave = () => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    let normalized;
    try {
      normalized = normalizeRangeForSave(dateTimeRangeRef.current ?? dateTimeRange, 'time');
    } catch (error) {
      alert(error?.message || 'Invalid recurrence settings.');
      return;
    }

    if (!normalized?.startDate) {
      alert('Start date and time is required.');
      return;
    }

    if (typeof onChange === 'function') {
      onChange(normalized);
    }

    setOpen(false);
  };

  // ----- RENDER ----- //

  return (
    <Modal
      title="Date & Time Range"
      isOpen={isOpen}
      width={isMobile ? '100%' : 660}
      height={isMobile ? '100%' : 520}
      onClose={() => setOpen(false)}
    >
      <ModalForm>

        {/* WRAPPER */}
        <div className="node-date-time-range-modal-wrapper">
          <NodeDateTimeRangeInput
            value={dateTimeRange}
            hybrid={false}
            autoFillEnd={false}
            onChange={handleDateTimeRangeChange}
          />
          <DateTimeRangeRepeatEditor
            value={dateTimeRange}
            onChange={handleRepeatChange}
          />
        </div>
      </ModalForm>

      <ModalButtons>
        <Button label="Save" onClick={handleSave} />
      </ModalButtons>
    </Modal>
  );
};

// Dev Tools
NodeDateTimeRangeModal.displayName = 'NodeDateTimeRangeModal';

export default NodeDateTimeRangeModal;
